import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, Checkbox, Grid, TextField } from '@mui/material';
import { handleWarning, highestPermissions } from '../../helpers/utils';
import { DIVISIONS, PROGRESS_OP, STATUS } from '../utils/lists';
import { useDispatch, useSelector } from 'react-redux';
import { getClients, setClients } from '../../actions/client';
import { cleanSearcher } from '../../actions/searcher';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import DateFnsUtils from '@date-io/date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import moment from 'moment';
import { format2Date } from '../../helpers/hours';
import { generateExcelOrders } from '../../actions/orders';

const FILTER_OPTIONS = [
	'Número de pedido',
	'Progreso',
	'División',
	'Estatus',
	'Activo/Cancelado',
	'Cliente',
];

const AUTOCOMPLETE_FIELDS = [
	'División',
	'Estatus',
	'Progreso',
	'Activo/Cancelado',
	'Cliente',
];

const lists = {
	Estatus: STATUS.map(({ label }) => label),
	División: DIVISIONS.map(({ label }) => label),
	Progreso: PROGRESS_OP.map(({ label }) => label),
	'Activo/Cancelado': ['Activo', 'Cancelado'],
};

const defaultSearchConfig = {
	searchField: null,
	selectedOption: null,
	progressOperation: null,
	page: 1,
};

export const Searcher2 = ({
	quotation = false,
	generateRows = null,
	handleClose = null,
	addFilterIfAdmin = null,
	initialStateRows = null,
	updateRows = null,
	setResetRows = null,
	editEndpoint = null,
}) => {
	// Searcher
	const [searcherOp, setSearcherOp] = useState(defaultSearchConfig);
	// Dispatch
	const dispatch = useDispatch();
	// Loading circle
	const [isLoading, setIsLoading] = useState(false);
	// Local clients
	const [clients, setClientsLocal] = useState([]);
	// Check
	const [usingDates, setUsingDates] = useState(false);
	// Redux clients
	const { clients: clientsRedux, searchConfig } = useSelector(
		(state) => state
	);
	// Dates
	const [firstDate, setFirstDate] = useState(null);
	const [secondDate, setSecondDate] = useState(null);

	const handleSearch = (config) => {
		// Reset cache page
		localStorage.setItem('lastTablePage', 1);

		if (config?.searchField === '' || config?.selectedOption === null) {
			handleWarning('Hacen falta campos para poder buscar');
		} else {
			// Save search config object in local storage
			localStorage.setItem('searchConfigPilu', JSON.stringify(config));

			// Prepare endpoint
			const endpoint = `/api/order/?ordering=-created_at&type=${
				quotation ? 'QUO' : 'ORD'
			}&${getSearchParam(config)}${addFilterIfAdmin(user)}`;

			// Page size
			const pageSizeParam = `&page_size=${initialStateRows.pageSize}`;

			// Prepare and format dates
			const date1 = format2Date(firstDate);
			const date2 = format2Date(secondDate);

			// Date range params
			const dateRangeParams =
				firstDate && secondDate
					? `&created_at__gte=${date1}T00:00:00Z&created_at__lt=${date2}T23:59:59Z`
					: '';

			// Final endpoint
			const finalEndpoint = endpoint + pageSizeParam + dateRangeParams;

			// Update search config (local & redux)
			const newConfig = {
				...initialStateRows,
				page: 1,
				endpoint: finalEndpoint,
			};
			updateRows(newConfig);
			// Generare rows
			generateRows(finalEndpoint, false, newConfig);
		}
	};

	// Get users from Redux or DB
	const handleGetClients = () => {
		// Check if redux has data
		if (clientsRedux.length > 0) {
			// Set clients to local state
			setClientsLocal(clientsRedux);
		} else {
			// Get data from DB
			getClients(null, 1000).then(({ data }) => {
				// Set clients to local state
				setClientsLocal(data.results);
				// Set clients to Redux
				setClients(data.results);
			});
		}
	};

	// Load search configuration
	useEffect(() => {
		const searchConfig = localStorage.getItem('searchConfigPilu');
		if (searchConfig !== 'null') {
			setIsLoading(true);
			const config = JSON.parse(searchConfig);
			// Set configuration
			setSearcherOp(config);
			setIsLoading(false);
		} else {
			setSearcherOp(defaultSearchConfig);
		}
	}, []);

	// Load clients
	useEffect(() => {
		if (searcherOp?.selectedOption === 'Cliente' && clients.length === 0) {
			handleGetClients();
		}
	}, [searcherOp?.selectedOption]);

	// User
	const { user } = useSelector((state) => state);

	const getSearchParam = (config) => {
		if (config?.selectedOption === 'Estatus') {
			const res = STATUS.find(
				({ label }) => label === config?.searchField
			).name;
			return `${res === 'FIN' ? 'progress__gte=100' : `search=${res}`}`;
		} else if (config?.selectedOption === 'División') {
			const res = DIVISIONS.find(
				({ label }) => label === config?.searchField
			).name;
			return `search=${res}`;
		} else if (config?.selectedOption === 'Progreso') {
			const operation = PROGRESS_OP.find(
				({ label }) => label === config?.progressOperation
			).name;
			if (operation === 'e') {
				return `search=${config?.searchField}`;
			} else {
				return `progress__${operation}=${config?.searchField}`;
			}
		} else if (config?.selectedOption === 'Activo/Cancelado') {
			return `canceled=${config?.searchField === 'Activo' ? 0 : 1}`;
		}
		if (config?.selectedOption === 'Cliente') {
			const clientId =
				clients.find(
					(client) => client.company_name === config?.searchField
				)?.id || 0;
			return `client_id__id=${clientId}`;
		} else {
			return `search=${config?.searchField}`;
		}
	};

	const TextFieldComponent = (params, label) => (
		<TextField
			fullWidth
			required
			margin='normal'
			{...params}
			label={label}
		/>
	);

	const handleGenerateExcel = async () =>
		await generateExcelOrders(
			searchConfig?.endpoint,
			firstDate,
			secondDate
		);

	const handleResetDates = () => {
		// Reset dates
		setFirstDate(null);
		setSecondDate(null);
	};

	return (
		<>
			<LocalizationProvider
				dateAdapter={AdapterDateFns}
				adapterLocale={es}
				utils={DateFnsUtils}
			>
				<Grid
					container
					component='main'
					spacing={1}
					style={{
						width:
							searcherOp?.selectedOption === 'Progreso'
								? '70%'
								: '50%',
						margin: '10px 0 15px 0',
					}}
				>
					<Grid
						item
						xs={searcherOp?.selectedOption === 'Progreso' ? 4 : 4.2}
					>
						<Autocomplete
							required
							fullWidth
							style={{
								width: '100%',
								margin: '-17px auto',
							}}
							value={searcherOp?.selectedOption || null}
							defaultValue={null}
							onChange={(_, value) => {
								setSearcherOp((old) => ({
									...old,
									searchField: null,
									selectedOption: value,
									progressOperation: null,
								}));
							}}
							options={
								FILTER_OPTIONS?.filter((option) => {
									if (option === 'División') {
										if (highestPermissions(user)) {
											return true;
										}
										return false;
									}
									return true;
								}) || []
							}
							renderInput={(params) =>
								TextFieldComponent(params, 'Buscar por')
							}
						/>
					</Grid>
					{AUTOCOMPLETE_FIELDS.includes(
						searcherOp?.selectedOption
					) && (
						<Grid item xs={4}>
							<Autocomplete
								required
								fullWidth
								style={{
									width: '100%',
									margin: '-17px auto',
								}}
								value={
									searcherOp?.selectedOption === 'Progreso'
										? searcherOp?.progressOperation || null
										: searcherOp?.searchField || null
								}
								defaultValue={null}
								onChange={(_, value) => {
									if (
										searcherOp?.selectedOption ===
										'Progreso'
									) {
										setSearcherOp((old) => ({
											...old,
											progressOperation: value,
										}));
									} else {
										setSearcherOp((old) => ({
											...old,
											searchField: value,
										}));
									}
								}}
								options={
									searcherOp?.selectedOption === 'Cliente'
										? clients?.map(
												(client) => client.company_name
										  ) || []
										: lists[searcherOp?.selectedOption] || []
								}
								renderInput={(params) =>
									TextFieldComponent(
										params,
										searcherOp?.selectedOption ===
											'Progreso'
											? 'Operación'
											: searcherOp?.selectedOption
									)
								}
							/>
						</Grid>
					)}

					{(!AUTOCOMPLETE_FIELDS.includes(
						searcherOp?.selectedOption
					) ||
						searcherOp?.selectedOption === 'Progreso') && (
						<Grid item xs={4}>
							<TextField
								required
								style={{ margin: '0 auto' }}
								label='Valor'
								value={
									searcherOp?.searchField === null
										? ''
										: searcherOp?.searchField
								}
								onChange={({ target }) => {
									setSearcherOp((old) => ({
										...old,
										searchField: target.value,
									}));
								}}
								onKeyDown={({ key }) => {
									if (key === 'Enter') {
										handleSearch(searcherOp);
									}
								}}
							/>
						</Grid>
					)}

					{usingDates && (
						<>
							<Grid item xs={4.5}>
								<DesktopDatePicker
									fullWidth
									label='Fecha inicial'
									inputFormat='dd/MM/yyyy'
									name='entry_date'
									value={firstDate}
									onChange={(val) => setFirstDate(val)}
									renderInput={(params) =>
										TextFieldComponent(
											params,
											'Fecha inicial'
										)
									}
								/>
							</Grid>

							<Grid item xs={4.5}>
								<DesktopDatePicker
									fullWidth
									label='Fecha final'
									inputFormat='dd/MM/yyyy'
									name='final_date'
									value={secondDate}
									onChange={(val) => setSecondDate(val)}
									renderInput={(params) =>
										TextFieldComponent(
											params,
											'Fecha final'
										)
									}
								/>
							</Grid>
						</>
					)}

					<Grid item xs={4}>
						<Checkbox
							checked={usingDates}
							onChange={() => {
								if (usingDates) {
									// Reset dates
									handleResetDates();
								}
								setUsingDates(!usingDates);
							}}
							color='primary'
						/>
						<label>Usar rango de fechas</label>
					</Grid>

					<Grid item xs={6}>
						<Button
							variant='text'
							onClick={() => handleSearch(searcherOp)}
						>
							Buscar
						</Button>

						<Button
							color='error'
							variant='text'
							onClick={() => {
								// Reset dates
								handleResetDates();
								// Reset search config (local & redux)
								dispatch(cleanSearcher());
								// Reset rows
								setResetRows(true);
								// Close search filters
								handleClose();
								// Reset searcher option config
								setSearcherOp(defaultSearchConfig);
								// Reset local storage
								localStorage.setItem(
									'searchConfigPilu',
									'null'
								);
								// Reset cache page
								localStorage.setItem('lastTablePage', 1);
							}}
						>
							Cerrar
						</Button>

						{searchConfig?.endpoint !== null && (
							<Button
								variant='text'
								color='success'
								onClick={handleGenerateExcel}
							>
								Descargar excel
							</Button>
						)}

						{isLoading && (
							<div style={{ margin: 'auto 0' }}>
								<p>
									<b>Cargando búsqueda guardada...</b>
								</p>
							</div>
						)}
					</Grid>
				</Grid>
			</LocalizationProvider>
		</>
	);
};
